/**
 * SwipeToSubmit — GSAP Draggable swipe slider for mobile form submission.
 * Extracted from the identical component duplicated across all 6 form modals.
 */

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { Loader2Icon } from 'lucide-react';
import { useGSAP } from '@gsap/react';
import type { SwipeToSubmitProps } from './types';
import { MODAL_COLORS } from './constants';

gsap.registerPlugin(Draggable);

export function SwipeToSubmit({
  onValidate,
  onSubmit,
  disabled,
  isPending,
  isFormValid,
  isHebrew,
  isRTL,
}: SwipeToSubmitProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const shimmerRef = useRef<HTMLDivElement>(null);
  const letterRefs = useRef<HTMLSpanElement[]>([]);
  const hintTimelineRef = useRef<gsap.core.Timeline | null>(null);
  const draggableRef = useRef<Draggable[]>([]);
  const submittedRef = useRef(false);
  const isDraggingRef = useRef(false);

  // Text shimmer when form is NOT valid
  useEffect(() => {
    if (!textRef.current || disabled || isPending || isFormValid) return;
    const shimmer = gsap.fromTo(
      textRef.current,
      { backgroundPosition: '200% center' },
      { backgroundPosition: '-200% center', duration: 2.5, repeat: -1, ease: 'none' },
    );
    return () => { shimmer.kill(); };
  }, [disabled, isPending, isFormValid]);

  // Hint animation when form IS valid
  useEffect(() => {
    const track = trackRef.current;
    const handle = handleRef.current;
    const shimmer = shimmerRef.current;
    if (!track || !handle || !shimmer) return;

    if (hintTimelineRef.current) {
      hintTimelineRef.current.kill();
      hintTimelineRef.current = null;
    }

    if (!isFormValid || disabled || isPending || isDraggingRef.current) {
      gsap.set(track, { borderColor: MODAL_COLORS.border });
      gsap.set(shimmer, { left: '-100%', opacity: 0 });
      gsap.set(handle, { clearProps: 'boxShadow' });
      gsap.set(handle, { boxShadow: '0 1px 3px rgba(0,0,0,0.1)' });
      letterRefs.current.forEach((el) => {
        if (el) gsap.set(el, { clearProps: 'color,textShadow,scale' });
      });
      return;
    }

    const tl = gsap.timeline({ repeat: -1, repeatDelay: 1.5 });
    hintTimelineRef.current = tl;

    tl.set(shimmer, { left: '-50%', opacity: 1 })
      .to(track, {
        borderColor: 'rgba(16, 185, 129, 0.6)',
        duration: 0.4, yoyo: true, repeat: 1, ease: 'sine.inOut',
      })
      .to(shimmer, { left: '200%', duration: 1.5, ease: 'power1.inOut' }, '-=0.4')
      .to(letterRefs.current.filter(Boolean), {
        color: '#ffffff',
        textShadow: '0 0 12px rgba(255, 255, 255, 0.9), 0 0 20px color-mix(in srgb, var(--color-primary-200) 50%, transparent)',
        scale: 1.08, stagger: 0.04, duration: 0.2, yoyo: true, repeat: 1, ease: 'sine.inOut',
      }, '-=1.3')
      .to(handle, {
        x: isRTL ? -10 : 10,
        duration: 0.3, yoyo: true, repeat: 1, ease: 'power2.inOut',
      }, '-=0.8')
      .to(handle, {
        boxShadow: '0 0 18px rgba(16, 185, 129, 0.5), 0 1px 3px rgba(0,0,0,0.1)',
        duration: 0.4, yoyo: true, repeat: 1, ease: 'sine.inOut',
      }, '-=1.0');

    return () => {
      tl.kill();
      hintTimelineRef.current = null;
      if (track) gsap.set(track, { borderColor: MODAL_COLORS.border });
      if (shimmer) gsap.set(shimmer, { left: '-100%', opacity: 0 });
      letterRefs.current.forEach((el) => {
        if (el) gsap.set(el, { clearProps: 'color,textShadow,scale' });
      });
    };
  }, [isFormValid, disabled, isPending, isRTL]);

  // Prevent native touch scrolling on track/handle
  useEffect(() => {
    const track = trackRef.current;
    const handle = handleRef.current;
    if (!track || !handle) return;
    const kill = (e: TouchEvent) => {
      if (e.cancelable) e.preventDefault();
      e.stopPropagation();
    };
    track.addEventListener('touchstart', kill, { passive: false });
    track.addEventListener('touchmove', kill, { passive: false });
    handle.addEventListener('touchstart', kill, { passive: false });
    handle.addEventListener('touchmove', kill, { passive: false });
    return () => {
      track.removeEventListener('touchstart', kill);
      track.removeEventListener('touchmove', kill);
      handle.removeEventListener('touchstart', kill);
      handle.removeEventListener('touchmove', kill);
    };
  }, []);

  // GSAP Draggable setup
  useGSAP(() => {
    const track = trackRef.current;
    const handle = handleRef.current;
    const glow = glowRef.current;
    const text = textRef.current;
    if (!track || !handle || !glow || !text) return;

    draggableRef.current.forEach((d) => d.kill());
    draggableRef.current = [];

    if (disabled || isPending) {
      gsap.set(handle, { x: 0 });
      gsap.set(glow, { x: isRTL ? track.offsetWidth : -track.offsetWidth });
      gsap.set(text, { opacity: 0.4 });
      return;
    }

    submittedRef.current = false;
    gsap.set(text, { opacity: 1 });

    const padding = 6;
    const maxDrag = track.offsetWidth - handle.offsetWidth - padding * 2;

    const [instance] = Draggable.create(handle, {
      type: 'x',
      lockAxis: true,
      allowNativeTouchScrolling: false,
      bounds: { minX: isRTL ? -maxDrag : 0, maxX: isRTL ? 0 : maxDrag },
      onPress(e: any) {
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();
        document.body.style.overflow = 'hidden';
        document.body.style.touchAction = 'none';
        isDraggingRef.current = true;
        if (hintTimelineRef.current) {
          hintTimelineRef.current.kill();
          hintTimelineRef.current = null;
        }
        gsap.set(track, { borderColor: MODAL_COLORS.border });
        if (shimmerRef.current) gsap.set(shimmerRef.current, { left: '-100%', opacity: 0 });
        letterRefs.current.forEach((el) => {
          if (el) gsap.set(el, { clearProps: 'color,textShadow,scale' });
        });
      },
      onDrag(e: any) {
        if (e && e.cancelable) e.preventDefault();
        const rawX = this.x;
        const progress = Math.abs(rawX) / maxDrag;
        if (isRTL) gsap.set(glow, { x: rawX + track.offsetWidth });
        else gsap.set(glow, { x: rawX - track.offsetWidth });
        gsap.set(text, { opacity: 1 - progress });
        gsap.set(handle, {
          boxShadow: `0 0 ${4 + progress * 12}px color-mix(in srgb, var(--color-primary-800) ${Math.round((0.2 + progress * 0.3) * 100)}%, transparent)`,
        });
      },
      onRelease() {
        isDraggingRef.current = false;
        if (!submittedRef.current) {
          document.body.style.overflow = '';
          document.body.style.touchAction = '';
        }
      },
      onDragEnd() {
        const progress = Math.abs(this.x) / maxDrag;
        const bounceBack = () => {
          document.body.style.overflow = '';
          document.body.style.touchAction = '';
          gsap.to(this.target, { x: 0, duration: 0.6, ease: 'elastic.out(1, 0.4)' });
          const resetGlowX = isRTL ? track.offsetWidth : -track.offsetWidth;
          gsap.to(glow, { x: resetGlowX, duration: 0.6, ease: 'elastic.out(1, 0.4)' });
          gsap.to(text, { opacity: 1, duration: 0.3 });
          gsap.to(handle, { boxShadow: '0 1px 3px rgba(0,0,0,0.1)', duration: 0.3 });
        };
        if (progress >= 0.9 && !submittedRef.current) {
          if (!onValidate()) {
            if (navigator.vibrate) navigator.vibrate([30, 50, 30]);
            bounceBack();
            return;
          }
          submittedRef.current = true;
          const endX = isRTL ? -maxDrag : maxDrag;
          gsap.to(handle, { x: endX, duration: 0.1 });
          gsap.to(track, { opacity: 0.5, duration: 0.2 });
          if (navigator.vibrate) navigator.vibrate(50);
          onSubmit();
        } else {
          bounceBack();
        }
      },
    });

    draggableRef.current = [instance];
    return () => { instance.kill(); };
  }, { dependencies: [disabled, isPending, isRTL, onSubmit, onValidate], scope: trackRef });

  // Reset after pending clears
  useEffect(() => {
    if (!isPending && handleRef.current && trackRef.current) {
      submittedRef.current = false;
      gsap.set(handleRef.current, { x: 0 });
      gsap.set(trackRef.current, { opacity: 1 });
      if (glowRef.current) {
        gsap.set(glowRef.current, {
          x: isRTL ? trackRef.current.offsetWidth : -trackRef.current.offsetWidth,
        });
      }
    }
  }, [isPending, isRTL]);

  const arrowChar = isRTL ? '\u2190' : '\u2192';
  const label = isPending
    ? (isHebrew ? '' : 'Submitting...')
    : (isHebrew ? '\u05D4\u05D7\u05DC\u05E7 \u05DC\u05E9\u05DC\u05D9\u05D7\u05D4' : 'Slide to Submit');

  return (
    <div
      ref={trackRef}
      className="relative w-full overflow-hidden select-none"
      style={{
        height: 56,
        borderRadius: 28,
        background: 'linear-gradient(135deg, var(--color-primary-700) 0%, var(--color-primary-600) 50%, var(--color-primary-700) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.15), 0 4px 16px color-mix(in srgb, var(--color-primary-500) 20%, transparent)',
        opacity: disabled ? 0.5 : 1,
        touchAction: 'none',
      }}
    >
      {/* Glow trail */}
      <div
        ref={glowRef}
        style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: 28,
          background: `linear-gradient(${isRTL ? '270deg' : '90deg'}, rgba(169,201,239,0) 0%, rgba(169,201,239,0.2) 50%, rgba(30,41,59,0.35) 80%, rgba(30,41,59,0.5) 100%)`,
          transform: `translateX(${isRTL ? '100%' : '-100%'})`, pointerEvents: 'none', zIndex: 1,
        }}
      />
      {/* Shimmer */}
      <div
        ref={shimmerRef}
        style={{
          position: 'absolute', top: 0, left: '-100%', width: '50%', height: '100%',
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent)',
          transform: 'skewX(-20deg)', pointerEvents: 'none', zIndex: 2, opacity: 0, borderRadius: 28,
        }}
      />
      {/* Label text */}
      <div
        ref={textRef}
        style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 14, letterSpacing: 0.5, pointerEvents: 'none', zIndex: 3, userSelect: 'none',
          ...(!isFormValid
            ? {
                background: 'linear-gradient(90deg, #888 0%, var(--color-primary-200) 35%, var(--color-primary-800) 50%, var(--color-primary-200) 65%, #888 100%)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }
            : {}),
        }}
      >
        {isPending ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Loader2Icon
              style={{ width: 16, height: 16, animation: 'spin 1s linear infinite', color: 'var(--color-primary-200)' }}
            />
            <span style={{ WebkitTextFillColor: '#999' }}>{label}</span>
          </span>
        ) : (
          <span style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            {label.split('').map((char, i) => (
              <span
                key={i}
                ref={(el) => { if (el) letterRefs.current[i] = el; }}
                style={{
                  display: 'inline-block',
                  color: isFormValid ? 'var(--color-primary-200)' : 'inherit',
                  whiteSpace: char === ' ' ? 'pre' : undefined,
                  transition: 'color 0.3s ease',
                }}
              >
                {char}
              </span>
            ))}
          </span>
        )}
      </div>
      {/* Drag handle */}
      <div
        ref={handleRef}
        style={{
          position: 'absolute', [isRTL ? 'right' : 'left']: 3, top: '50%', transform: 'translateY(-50%)',
          width: 50, height: 50, borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--color-primary-300) 0%, var(--color-primary-500) 100%)',
          border: `1px solid ${MODAL_COLORS.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: disabled ? 'not-allowed' : 'grab', zIndex: 3,
          fontSize: 22, fontWeight: 'bold', color: '#ffffff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)', touchAction: 'none',
        }}
      >
        {arrowChar}
      </div>
    </div>
  );
}
